// API client: session (tokens), authenticated calls with automatic refresh, readable errors.
import { ApiError } from '@gruvetto/browser';
import type { AuthResponse, MailLang, PublicUser } from '@gruvetto/contracts';

export { ApiError };
import { t } from '../i18n/index.js';

const KEY = 'gv-auth-v1';

export interface Session { user: PublicUser; accessToken: string; refreshToken: string; accessExp: number }

let session: Session | null = (() => { try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch { return null; } })();
const listeners = new Set<(s: Session | null) => void>();

/** API base: build variable, otherwise the same origin as the app. */
export const apiBase = (): string => (import.meta.env.VITE_API_URL || '') + '/api';

export const currentUser = (): PublicUser | null => session?.user ?? null;
/** Watts typed on the keyboard are an administrator's test tool: every other rider needs a trainer or a power meter. */
export const canTypeWatts = (): boolean => currentUser()?.role === 'admin';
export const isLoggedIn = (): boolean => !!session;
export const accessToken = (): string | null => session?.accessToken ?? null;
export function onAuthChange(fn: (s: Session | null) => void) { listeners.add(fn); return () => listeners.delete(fn); }

function setSession(s: Session | null) {
  session = s;
  try { if (s) localStorage.setItem(KEY, JSON.stringify(s)); else localStorage.removeItem(KEY); } catch {}
  for (const fn of listeners) fn(s);
}
const fromAuth = (r: AuthResponse): Session => ({ user: r.user, accessToken: r.accessToken, refreshToken: r.refreshToken, accessExp: Date.now() + (r.expiresIn - 30) * 1000 });

async function raw(path: string, init: RequestInit = {}, token?: string | null): Promise<Response> {
  const headers: Record<string, string> = { ...(init.headers as Record<string, string>) };
  if (init.body && !headers['content-type']) headers['content-type'] = 'application/json';
  if (token) headers.authorization = `Bearer ${token}`;
  try { return await fetch(apiBase() + path, { ...init, headers }); } catch { throw new ApiError(0, t.sync.unreachable); }
}

let refreshing: Promise<boolean> | null = null;
async function refresh(): Promise<boolean> {
  if (!session) return false;
  refreshing ??= (async () => {
    const r = await raw('/auth/refresh', { method: 'POST', body: JSON.stringify({ refreshToken: session!.refreshToken }) });
    if (r.ok) { setSession(fromAuth(await r.json())); return true; }
    if (r.status === 401) setSession(null);
    return false;
  })().finally(() => { refreshing = null; });
  return refreshing;
}

/**
 * JSON call. `auth`: attaches the token (and refreshes it if needed). `keepalive`: the request outlives the page (a
 * last upload while it closes); it leaves at once with the token in hand, since a refresh first would never get to send it.
 */
export async function api<T = unknown>(path: string, { method = 'GET', body, auth = true, keepalive = false }: { method?: string; body?: unknown; auth?: boolean; keepalive?: boolean } = {}): Promise<T> {
  if (auth && session && Date.now() > session.accessExp && !keepalive) await refresh();
  let r = await raw(path, { method, body: body === undefined ? undefined : JSON.stringify(body), ...(keepalive ? { keepalive } : {}) }, auth ? session?.accessToken : null);
  if (keepalive) return undefined as T;
  if (r.status === 401 && auth && session && (await refresh())) r = await raw(path, { method, body: body === undefined ? undefined : JSON.stringify(body) }, session?.accessToken);
  if (r.status === 204) return undefined as T;
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new ApiError(r.status, data.error || t.sync.httpError(r.status), data);
  return data as T;
}

/** Binary call (replays): sends bytes as application/octet-stream, returns the response bytes. */
export async function apiBytes(path: string, { method = 'GET', body }: { method?: string; body?: Uint8Array } = {}): Promise<ArrayBuffer> {
  if (session && Date.now() > session.accessExp) await refresh();
  const init = (): RequestInit => ({ method, body: body ? new Blob([body as BlobPart]) : undefined, headers: body ? { 'content-type': 'application/octet-stream' } : {} });
  let r = await raw(path, init(), session?.accessToken);
  if (r.status === 401 && session && (await refresh())) r = await raw(path, init(), session?.accessToken);
  if (!r.ok) { const data = await r.json().catch(() => ({})); throw new ApiError(r.status, data.error || t.sync.httpError(r.status)); }
  return r.arrayBuffer();
}

/** The form only submits with the terms box ticked: the server refuses a sign-up without `acceptTerms`. */
export async function register(email: string, password: string, name: string) { setSession(fromAuth(await api<AuthResponse>('/auth/register', { method: 'POST', body: { email, password, name, acceptTerms: true }, auth: false }))); }
export async function login(email: string, password: string) { setSession(fromAuth(await api<AuthResponse>('/auth/login', { method: 'POST', body: { email, password }, auth: false }))); }
/** Asks for a password reset e-mail (ADR 0018). The server answers the same whether the address has an account or not. */
export async function forgotPassword(email: string, lang: 'en' | 'fr') { await api('/auth/forgot', { method: 'POST', body: { email, lang }, auth: false }); }
/** New password from the e-mailed link: the server closes every other session and opens this one. */
export async function resetPassword(token: string, password: string, lang: MailLang) { setSession(fromAuth(await api<AuthResponse>('/auth/reset', { method: 'POST', body: { token, password, lang }, auth: false }))); }
/** Changes the password (ADR 0030): the current one proves it is the owner; every other session is closed, this one is named so it stays. */
export async function changePassword(current: string, password: string, lang: MailLang) {
  await api('/me/password', { method: 'POST', body: { current, password, refreshToken: session?.refreshToken, lang } });
}
/** Asks for the e-mailed link that sets a first password on an account created with Google or Strava. */
export async function requestPasswordLink(lang: MailLang) { await api('/me/password/link', { method: 'POST', body: { lang } }); }
export async function logout() {
  const s = session;
  setSession(null);
  if (s) await api('/auth/logout', { method: 'POST', body: { refreshToken: s.refreshToken }, auth: false }).catch(() => {});
}
/** Session obtained other than by e-mail (return from Google or Strava). */
export function adoptSession(r: AuthResponse) { setSession(fromAuth(r)); }
export function updateUser(u: Partial<PublicUser>) { if (session) setSession({ ...session, user: { ...session.user, ...u } }); }
